import type { ImportViolation } from '@/lib/api/generated/model';

import type { BuilderSelection } from './selection';

/**
 * Which of the selected element's fields the service refused, by field name.
 *
 * The inspector shows one element at a time, so this narrows the whole refusal
 * to the element on screen and keys what is left by `field` — the locator the
 * service added for exactly this (`ImportViolation.field`). Parsing `path`
 * would work today and break silently the first time a message's path was
 * rewritten; the field name is the part that is promised.
 *
 * Violations with no `field` are deliberately dropped here rather than guessed
 * at. "This step is not assigned to a phase" is about the step's placement and
 * "no path reaches done" is about the graph — neither has an input to mark, and
 * both are still reported in full by `ProblemList`, which is what makes
 * dropping them safe.
 */
export function fieldErrorsFor(
  violations: ImportViolation[],
  selection: BuilderSelection,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const violation of violations) {
    if (!violation.field) continue;
    if (!violationTargets(violation, selection)) continue;
    // First one wins: two rules on one field would otherwise render as
    // whichever came last, which is arbitrary. The list has both.
    errors[violation.field] ??= violation.message;
  }
  return errors;
}

/**
 * Whether this violation is about the selected element.
 *
 * Exported because the same question decides which error to *clear* when a
 * field is edited, and answering it twice in two places is how the panel and
 * the problem list would drift apart.
 */
export function violationTargets(
  violation: ImportViolation,
  selection: BuilderSelection,
): boolean {
  if (!selection) {
    // Nothing selected means the workflow itself, whose violations carry no
    // element locator at all.
    return (
      !violation.step_id && !violation.transition_key && !violation.phase_id
    );
  }
  switch (selection.kind) {
    case 'step':
      // A transition's violation carries its source step's id too, so the step
      // panel has to exclude them — otherwise a missing condition would mark a
      // field on the step that owns the transition.
      return violation.step_id === selection.id && !violation.transition_key;
    case 'transition':
      return (
        violation.step_id === selection.stepId &&
        violation.transition_key === selection.key
      );
    case 'phase':
      return violation.phase_id === selection.id;
  }
}
